/**
 * On-demand load of a study group's shared cards, one deck at a time — the
 * client half of the `lazySgCards` capability (`client-capabilities.ts`).
 *
 * Under that flag the group's `sg-sync-init` reply carries a deck this client
 * has never loaded as properties alone; its cards stay on the server until
 * something here needs them. On mobile that is exactly one thing: minting the
 * member's personal MIRROR of a deck that has none yet (`use-seed-sg-mirror.ts`)
 * — tiles count from the mirrors' summaries and the deck screen opens the
 * mirror, so an existing mirror never needs the shared set in memory.
 *
 * The wire is `sg-request-deck-cards { deckId }`; the server answers with the
 * same `sg-cards-bulk-updated` frames a sync-init stream uses and then, always,
 * `sg-decks-loaded { deckIds: [deckId] }` — so "complete" is observed as the
 * deck entering the slice's `loadedDeckIds`, the one flag the whole SG store
 * already keys on. No second awaiter registry: `requestSgDeckCards` subscribes
 * to the store and resolves on that flag, whichever message set it (the
 * requested reply, a sync-init stream, a peer's deck push).
 *
 * With the flag OFF nothing here sends: every stale deck streams with the
 * sync-init reply and is loaded when its completion frame lands, so the hook
 * merely reports "still streaming" until then.
 */

import { getSyncClientCapabilities } from '@flashka/shared/sync-engine/framework/client-capabilities';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { enqueueForGroup } from '../internal/hook/study-group-sender-registry';
import {
  type StudyGroupSlice,
  useStudyGroupStore,
} from '../internal/store/study-group-store';

/** Client→server: load one deck's shared cards. Replied to the sender only. */
export type SGRequestDeckCardsMsg = {
  type: 'sg-request-deck-cards';
  deckId: string;
};

/**
 * How long a request may go WITHOUT PROGRESS before it is given up (and may be
 * retried). A large deck on a slow link keeps resetting it with every frame, so
 * a slow stream is never asked for a second time while its frames still arrive.
 * Matches the personal `rehydrateColdDeck`.
 *
 * PROGRESS IS MEASURED PER BATCH, NOT PER DECK. The server serves
 * `sg-request-deck-cards` strictly one deck at a time (its input gate), and the
 * first open of a group fires one request per un-mirrored deck. Measured on the
 * deck's OWN bucket, every deck queued behind a slow one timed out while the
 * server was still busy ahead of it; the hook re-asked (there is no server-side
 * dedupe) until the budget ran out, and the member watched a spinner turn into
 * "decks unavailable" for decks that were on their way. A frame for ANY deck of
 * the same group's in-flight batch — or one of them completing — re-arms them
 * all: that is the socket visibly working through the queue.
 */
export const SG_DECK_CARDS_REQUEST_TIMEOUT_MS = 10_000;

/** Failed requests the hook makes for one pending set before it stops asking
 *  (until the group reconnects or the set changes). */
export const SG_DECK_CARDS_MAX_ATTEMPTS = 5;

/** Wait before the hook's n-th retry (1-based): 1 s, 2 s, 4 s, … capped. */
export function sgDeckCardsRetryDelayMs(attempt: number): number {
  return Math.min(1_000 * 2 ** Math.max(0, attempt - 1), 30_000);
}

/**
 * In-flight requests, each with the teardown that releases the two handles it
 * holds open — the no-progress timer and the store subscription. The teardown
 * is what the test seam needs: dropping the map entry alone would leave a
 * 10 s timer armed in a shared Jest worker, to fire inside whatever suite ran
 * next (the hazard `_resetStudyGroupPersistence` exists for on the SG store).
 */
const inflight = new Map<
  string,
  { groupId: string; promise: Promise<void>; arm: () => void; release: () => void }
>();
const flightKey = (groupId: string, deckId: string) => `${groupId}\x01${deckId}`;

/** Restart the no-progress timer of every request still out for this group. */
function armGroupRequests(groupId: string): void {
  for (const entry of inflight.values()) {
    if (entry.groupId === groupId) entry.arm();
  }
}

/** The decks in `deckIds` whose complete shared card set is NOT in memory. */
export function pendingSgDeckLoads(
  slice: StudyGroupSlice | undefined,
  deckIds: readonly string[],
): string[] {
  const loaded = slice?.loadedDeckIds;
  return deckIds.filter((id) => !loaded?.has(id));
}

/**
 * Ask the group's server for `deckId`'s shared cards and resolve once the deck
 * is complete in the SG store — or once it is GONE from the group (unshared
 * while the request was out: there is nothing left to load, and the server's
 * completion frame for a deck this client no longer knows is ignored). Resolves
 * at once for a deck already loaded; concurrent callers for the same deck share
 * one request. Rejects when the deck is still not loaded after `timeoutMs` of
 * NO PROGRESS, or when the group's socket drops while the request is out (its
 * frames may have been cut short) — callers retry on the next connect
 * (`useHydrateSgDeckCards` does). A request made before the socket is up is
 * queued by the sender registry and goes out on connect.
 */
export function requestSgDeckCards(
  groupId: string,
  deckId: string,
  opts?: { timeoutMs?: number },
): Promise<void> {
  const state = useStudyGroupStore.getState();
  if (state.groups[groupId]?.loadedDeckIds.has(deckId)) return Promise.resolve();
  const key = flightKey(groupId, deckId);
  const existing = inflight.get(key);
  if (existing) return existing.promise;

  const timeoutMs = opts?.timeoutMs ?? SG_DECK_CARDS_REQUEST_TIMEOUT_MS;
  let settled = false;
  let unsubscribe: (() => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Assigned by the executor below; held out here so the `inflight` entry (and
  // through it `armGroupRequests`) can restart this request's timer.
  let arm: () => void = () => {};
  const promise = new Promise<void>((resolve, reject) => {
    const finish = (err?: Error) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      unsubscribe?.();
      inflight.delete(key);
      if (err) {
        reject(err);
        return;
      }
      // This deck landing is progress for the batch behind it: the server is
      // serving them one at a time, so the next one starts about now.
      armGroupRequests(groupId);
      resolve();
    };
    arm = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(
        () => finish(new Error(`sg-request-deck-cards ${groupId}/${deckId}: timed out`)),
        timeoutMs,
      );
    };
    let wasConnected = state.groups[groupId]?.connectionStatus === 'connected';
    let lastBucket = state.groups[groupId]?.cards[deckId];
    // Was the deck in this client's view of the group when we asked? If it then
    // leaves (a tombstone drops it from `deckProps`), the request is moot.
    const knownAtStart = !!state.groups[groupId]?.deckProps[deckId];
    unsubscribe = useStudyGroupStore.subscribe((s) => {
      const slice = s.groups[groupId];
      if (slice?.loadedDeckIds.has(deckId)) {
        finish();
        return;
      }
      if (knownAtStart && slice && !slice.deckProps[deckId]) {
        finish();
        return;
      }
      const connected = slice?.connectionStatus === 'connected';
      if (connected) wasConnected = true;
      else if (wasConnected) {
        finish(new Error(`sg-request-deck-cards ${groupId}/${deckId}: socket dropped`));
        return;
      }
      // A frame landed in the deck's bucket: the reply is progressing — and so
      // is every request queued behind it on the same socket.
      const bucket = slice?.cards[deckId];
      if (bucket !== lastBucket) {
        lastBucket = bucket;
        arm();
        armGroupRequests(groupId);
      }
    });
    arm();
    const msg: SGRequestDeckCardsMsg = { type: 'sg-request-deck-cards', deckId };
    enqueueForGroup(groupId, JSON.stringify(msg));
  });
  // A listener firing inside the constructor cannot precede this line: the
  // subscription only runs on a store change, and the send is asynchronous.
  if (!settled)
    inflight.set(key, {
      groupId,
      promise,
      arm: () => {
        if (!settled) arm();
      },
      release: () => {
        if (timer !== undefined) clearTimeout(timer);
        timer = undefined;
        unsubscribe?.();
      },
    });
  return promise;
}

const NO_PENDING: string[] = [];

/**
 * Bring `sgDeckIds`' shared cards into the SG store and report the wait.
 *
 * TWO answers, like `useHydrateColdDecks`. `isLoading` means the shared sets
 * are not here YET; `unavailable` means they are not here and nothing is still
 * trying — the group is offline, or the request budget is spent. A surface
 * that had only `isLoading` could not tell the two apart, so a member opening
 * a shared deck offline got a spinner that never ended (and every mirror-keyed
 * list silently rendered its "nothing here" empty state instead).
 *
 * Under `lazySgCards` the unloaded ones are requested once the group is
 * connected; after a timeout or a dropped socket they are requested again with
 * exponential backoff (`sgDeckCardsRetryDelayMs`), at most
 * `SG_DECK_CARDS_MAX_ATTEMPTS` times per pending set — then the hook stops
 * asking until the group reconnects or the set changes, so a server without
 * the wire is never polled forever. With the flag off the sync-init stream is
 * what loads them and the hook only reports. Consumers put `isLoading` in
 * their effect deps so a guarded mint/merge re-runs once the set is complete.
 */
export function useHydrateSgDeckCards(
  groupId: string,
  sgDeckIds: readonly string[],
): { isLoading: boolean; unavailable: boolean } {
  const pending = useStudyGroupStore(
    useShallow((s) => {
      const ids = pendingSgDeckLoads(s.groups[groupId], sgDeckIds);
      return ids.length > 0 ? ids : NO_PENDING;
    }),
  );
  const status = useStudyGroupStore((s) => s.groups[groupId]?.connectionStatus);
  const connected = status === 'connected';
  const [retryTick, setRetryTick] = useState(0);
  // The pending set this hook has stopped asking for. A ref cannot answer it:
  // the render needs to know, and giving up must repaint the surface.
  const [gaveUpKey, setGaveUpKey] = useState<string | null>(null);
  // A group slice starts at 'disconnected' before its socket is even armed, so
  // that status alone cannot mean "offline" — it would flash an error on the
  // first commit. It only means offline once the socket has been live.
  const [sawLive, setSawLive] = useState(false);
  const attempts = useRef(0);
  const pendingKey = pending.join(',');

  useEffect(() => {
    if (status === 'connecting' || status === 'connected') setSawLive(true);
  }, [status]);

  // A new pending set or a (re)connect starts the attempt budget over.
  useEffect(() => {
    attempts.current = 0;
    setGaveUpKey(null);
  }, [groupId, pendingKey, connected]);

  useEffect(() => {
    if (!connected || pendingKey === '' || !getSyncClientCapabilities().lazySgCards)
      return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const ids = pendingKey.split(',');
    void Promise.allSettled(ids.map((id) => requestSgDeckCards(groupId, id))).then(
      (results) => {
        if (cancelled || !results.some((r) => r.status === 'rejected')) return;
        // A timeout or a dropped socket: ask again after a backoff (the loaded
        // ones dropped out of `pending`, so only what is still missing goes
        // out) — up to the attempt budget.
        attempts.current += 1;
        if (attempts.current >= SG_DECK_CARDS_MAX_ATTEMPTS) {
          console.warn(
            `[sg-deck-cards] ${groupId}: gave up loading ${ids.length} deck(s) after ` +
              `${attempts.current} attempts; retrying on the next connect`,
          );
          setGaveUpKey(pendingKey);
          return;
        }
        retry = setTimeout(
          () => setRetryTick((t) => t + 1),
          sgDeckCardsRetryDelayMs(attempts.current),
        );
      },
    );
    return () => {
      cancelled = true;
      if (retry !== undefined) clearTimeout(retry);
    };
  }, [groupId, pendingKey, connected, retryTick]);

  const offline =
    sawLive && (status === 'disconnected' || status === 'error' || status === 'kicked');
  const unavailable = pending.length > 0 && (gaveUpKey === pendingKey || offline);
  const isLoading = pending.length > 0 && !unavailable;
  return useMemo(() => ({ isLoading, unavailable }), [isLoading, unavailable]);
}

/**
 * Test seam: drop every in-flight request AND release the handles it holds —
 * its no-progress timer and its store subscription. Awaiters stay pending on
 * purpose (settling them would surface as an unhandled rejection in whichever
 * suite runs next, which is the very thing this seam removes).
 */
export function _resetSgDeckCardLoads(): void {
  for (const entry of inflight.values()) entry.release();
  inflight.clear();
}
